import { Modal } from "react-bootstrap";
import "./InvoiceAdminCardDetalle.scss";
import "./RequestInvoiceModal.scss";
import { useEffect, useState } from "react";
import { InvoiceData, uploadInvoiceFireBase } from "@services/InvoiceServices";
import useCollaboratorLogs, { collaborator, user } from "@hooks/collaborators/useCollaboratorLogs";
import {
	AmountMode,
	InvoiceTaxConfig,
	computeBreakdownFor,
	defaultTaxConfig,
	isPersonaMoral,
	validateTaxConfig,
} from "@services/invoiceTaxes";
import { MontoBar } from "./components/InvoiceTaxes/MontoBar";
import { USOS_CFDI } from "./usoCfdi";
import { FISCAL_REGIMES } from "./fiscalRegimes";

// "Nueva solicitud" del panel de Facturación: mismo diseño que la solicitud abierta
// (InvoiceAdminCard, clases iac2-*). La solicitud se guarda como "Pending" y se timbra al aprobarla.
export default function RequestInvoiceModal(props: any) {
	const [loader, setLoader] = useState<boolean>(false);
	const { invoiceLog } = useCollaboratorLogs();
	const [rfc, setRfc] = useState<string>("");
	const [amountText, setAmountText] = useState<string>(props.invoiceAmount ? String(props.invoiceAmount) : "");
	const [amountMode, setAmountMode] = useState<AmountMode>("total");
	const [taxCfg, setTaxCfg] = useState<InvoiceTaxConfig>(defaultTaxConfig(""));

	const rfcIsMoral = isPersonaMoral(rfc);
	useEffect(() => {
		setTaxCfg(defaultTaxConfig(rfc));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rfcIsMoral]);

	useEffect(() => {
		if (props.show) {
			setRfc("");
			setAmountText(props.invoiceAmount ? String(props.invoiceAmount) : "");
			setAmountMode("total");
			setTaxCfg(defaultTaxConfig(""));
		}
	}, [props.show, props.invoiceAmount]);

	const breakdown = !validateTaxConfig(taxCfg) ? computeBreakdownFor(amountMode, parseFloat(amountText), taxCfg) : null;

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!breakdown) {
			alert("Revisa el monto y los impuestos: no se pudo calcular el desglose.");
			return;
		}
		setLoader(true);
		const form = e.target;
		let data: InvoiceData = {
			createdAt: new Date(),
			requestDate: new Date(),
			fiscalRegime: form.fiscalRegime.value,
			street: form.street.value,
			city: form.city.value,
			state: form.state.value,
			name: form.name.value,
			email: form.email.value,
			paymentForm: form.paymentMethod.value,
			use: form.invoiceUse.value,
			rfc: form.rfc.value.toUpperCase(),
			zipCode: form.zipCode.value,
			corporateName: form.corporateName.value.toUpperCase(),
			// Siempre se guarda lo que pagará el cliente (total) y su subtotal.
			amount: breakdown.total,
			netPrice: breakdown.subtotal,
			taxConfig: taxCfg,
			status: "Pending",
		};
		uploadInvoiceFireBase(data).then(async (res) => {
			if (res !== null) {
				alert("Solicitud creada con éxito.");
				if (props.collaborator) {
					let tempCollaborator: collaborator = {
						name: props.collaborator.name,
						id: props.collaborator.id,
						role: props.collaborator.collaborator_rol ?? "",
					};
					let tempUser: user = { name: form.name.value, id: "" };
					await invoiceLog("request", tempCollaborator, tempUser);
				}
				props.getInvoices();
				props.setCategorie(2);
				props.onHide();
			}
			setLoader(false);
		});
	};

	return (
		<Modal show={props.show} onHide={props.onHide} dialogClassName="nr-modal" contentClassName="nr-modal-content" centered>
			<form onSubmit={handleSubmit} className="iac2 nr">
				<div className="iac2-header">
					<div className="iac2-title">
						<span className="iac2-name">Nueva solicitud de factura</span>
					</div>
					<div className="iac2-header-actions">
						<button className="iac2-close" onClick={props.onHide} type="button" aria-label="Cerrar">
							×
						</button>
					</div>
				</div>

				<div className="iac2-body">
					<div className="iac2-data">
						<section className="iac2-section">
							<h4>Datos fiscales</h4>
							<div className="iac2-grid">
								<label className="iac2-field wide">
									<span>Nombre</span>
									<input type="text" name="name" required defaultValue={props.userInvoiceInfo?.name ?? ""} maxLength={50} />
								</label>
								<label className="iac2-field">
									<span>RFC</span>
									<input
										type="text"
										name="rfc"
										required
										maxLength={16}
										onChange={(e) => setRfc(e.target.value)}
										style={{ textTransform: "uppercase" }}
									/>
								</label>
								<label className="iac2-field">
									<span>Correo</span>
									<input type="email" name="email" required defaultValue={props.userInvoiceInfo?.email ?? ""} maxLength={50} />
								</label>
								<label className="iac2-field wide">
									<span>Razón social</span>
									<input type="text" name="corporateName" required maxLength={70} />
									<small>Sin el régimen societario (ej.: sin «S.A. de C.V.»).</small>
								</label>
								<label className="iac2-field wide">
									<span>Régimen fiscal</span>
									<select name="fiscalRegime" defaultValue={""} required>
										<option value="" disabled>
											Selecciona una opción
										</option>
										{FISCAL_REGIMES.map(([code, text]) => (
											<option key={code} value={code}>
												({code}) {text}
											</option>
										))}
									</select>
								</label>
							</div>
						</section>

						<section className="iac2-section">
							<h4>Domicilio fiscal</h4>
							<div className="iac2-grid">
								<label className="iac2-field wide">
									<span>Calle <i className="iac2-opt">(opcional)</i></span>
									<input type="text" name="street" maxLength={50} />
								</label>
								<label className="iac2-field">
									<span>Ciudad <i className="iac2-opt">(opcional)</i></span>
									<input type="text" name="city" maxLength={50} />
								</label>
								<label className="iac2-field">
									<span>Estado <i className="iac2-opt">(opcional)</i></span>
									<input type="text" name="state" maxLength={30} />
								</label>
								<label className="iac2-field">
									<span>Código postal</span>
									<input type="text" name="zipCode" required maxLength={11} />
								</label>
							</div>
						</section>

						<section className="iac2-section">
							<h4>Factura</h4>
							<div className="iac2-grid">
								<label className="iac2-field wide">
									<span>Uso de factura</span>
									<select name="invoiceUse" defaultValue={""} required>
										<option value="" disabled>
											Selecciona una opción
										</option>
										{USOS_CFDI.map(([code, text]) => (
											<option key={code} value={code}>
												{code} · {text}
											</option>
										))}
									</select>
								</label>
								<label className="iac2-field wide">
									<span>Método de pago</span>
									<select name="paymentMethod" defaultValue={""} required>
										<option value="" disabled>
											Selecciona una opción
										</option>
										<option value="01">Efectivo</option>
										<option value="03">Oxxo Pay</option>
										<option value="03">SPEI</option>
										<option value="03">Transferencia</option>
										<option value="03">Depósito Bancario</option>
										<option value="04">Tarjeta</option>
										<option value="04">Paypal</option>
										<option value="03">Otro</option>
									</select>
								</label>
							</div>
						</section>
					</div>

					<aside className="iac2-money">
						<MontoBar
							amountInputProps={{
								name: "invoiceAmount",
								value: amountText,
								disabled: !!props.invoiceAmount,
								onChange: (e) => setAmountText(e.target.value),
							}}
							amount={parseFloat(amountText)}
							mode={amountMode}
							setMode={setAmountMode}
							rfc={rfc}
							value={taxCfg}
							onChange={setTaxCfg}
						/>
					</aside>
				</div>

				<div className="iac2-footer">
					<div className="iac2-footer-left" />
					<div className="iac2-footer-right">
						<button className="iac2-btn ghost" onClick={props.onHide} type="button">
							Descartar
						</button>
						<button className="iac2-btn primary" type="submit" disabled={loader}>
							{loader ? "Guardando…" : "Crear solicitud"}
						</button>
					</div>
				</div>
			</form>
		</Modal>
	);
}
